// Popover showing the actual stop list for a run — what flights, in what
// order, with bag counts and connection time. Mirrors the dispatcher's
// mental model for "what does this run actually look like on the ground".

import React from 'react';
import { Popover, Box, Typography } from '@mui/material';
import { C } from '../styles';

function categoryColor(cat) {
  if (cat === 'hot')      return C.hot;
  if (cat === 'priority') return C.warn;
  if (cat === 'cold')     return C.cold;
  return C.textSecondary;
}

export default function RunStopsPopover({ anchorEl, run, onClose }) {
  if (!run) return null;
  const num = (run.inboundFlightNumber || '').replace(/^WN\s*/, '');
  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: C.surface,
            backdropFilter: C.blur,
            WebkitBackdropFilter: C.blur,
            border: `1px solid ${C.borderStrong}`,
            borderRadius: 1.5,
            boxShadow: '0 16px 28px rgba(0, 0, 0, 0.45)',
            color: C.textPrimary,
            minWidth: 320,
            maxWidth: 380,
          },
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        px: 1.5, py: 1.25,
        borderBottom: `1px solid ${C.divider}`,
        display: 'flex', alignItems: 'center', gap: 1,
      }}>
        <Typography sx={{ fontSize: '1rem', fontWeight: 'bold', color: C.textPrimary }}>
          WN {num}
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', color: C.textMuted }}>
          {run.inboundFrom} → {run.inboundGate} · {run.inboundEta}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{
          fontSize: '0.72rem', color: C.textSecondary, fontWeight: 'bold',
          backgroundColor: C.surfaceAlt, px: 0.75, py: 0.25, borderRadius: 0.75,
        }}>
          {run.slotLabel}
        </Typography>
      </Box>

      {/* Stops */}
      <Box sx={{ px: 1, py: 0.75 }}>
        {run.isFreightOnly ? (
          <Typography sx={{
            px: 1, py: 1, fontSize: '0.85rem', color: C.freight,
            fontWeight: 'bold', textAlign: 'center',
          }}>
            Freight-only run · no bag stops
          </Typography>
        ) : run.stops.map((stop, i) => {
          const stopNum = (stop.flightNumber || '').replace(/^WN\s*/, '');
          const cColor = categoryColor(stop.connectionCategory);
          return (
            <Box key={i} sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              px: 1, py: 0.6,
              borderBottom: i === run.stops.length - 1 ? 'none' : `1px solid ${C.divider}`,
            }}>
              <Typography sx={{
                fontSize: '0.7rem', color: C.textMuted, fontWeight: 'bold',
                width: 18, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
              }}>
                {i + 1}
              </Typography>
              <Box sx={{
                width: 8, height: 8, borderRadius: '50%', backgroundColor: cColor,
              }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: '0.82rem', color: C.textPrimary, fontWeight: 'bold' }}>
                  {stopNum}
                  <Box component="span" sx={{ color: C.textSecondary, fontWeight: 'normal', ml: 0.75 }}>
                    → {stop.destination}
                  </Box>
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: C.textMuted }}>
                  {stop.gate} · ETD {stop.etd} · {stop.connectionTime}m
                </Typography>
              </Box>
              <Typography sx={{
                fontSize: '0.78rem', color: C.textSecondary, fontWeight: 'bold',
                fontVariantNumeric: 'tabular-nums',
              }}>
                {stop.bags} bags
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Popover>
  );
}
